import { FC } from "react";
import { Box, Typography } from "@mui/material";
import HPButton from "@/components/common/Button";
import type { SharedTimeConfig } from "@utils/tab-time-controls";

interface TabTimeBreakWarningProps {
  /**
   * The shared config the in-progress edit would break, or null when there is
   * no valid alignment to break. Computed once by the parent section and shared
   * with the alignment callout to avoid a duplicate break computation.
   */
  broken: { shared: SharedTimeConfig } | null;
  /** Revert only the saved time setting + viewer-time contract to the shared config. */
  onRestore: (config: SharedTimeConfig) => void;
}

/**
 * Non-blocking pre-save warning (Module 4). Shown while the in-progress edit
 * would break a valid shared tab-time configuration this chart is part of. Lets
 * the researcher restore the shared configuration (saved time setting + contract
 * only) or ignore it and keep editing / save the divergent config.
 */
export const TabTimeBreakWarning: FC<TabTimeBreakWarningProps> = ({
  broken,
  onRestore,
}) => {
  if (!broken) return null;

  return (
    <Box
      sx={{
        borderRadius: 1,
        border: "1px solid var(--color-hp-med-yellow)",
        backgroundColor: "var(--color-hp-light-yellow)",
        px: 1.5,
        py: 1.25,
        mb: 1.5,
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: "var(--color-hp-dark-yellow)",
        }}
      >
        This change breaks the tab time alignment
      </Typography>
      <Typography sx={{ fontSize: 12, color: "#475467" }}>
        These charts currently share one tab time configuration. Saving this
        change will disable the Tab Time Controls section for this family and
        show “Mixed configuration” until the charts are realigned.
      </Typography>
      <Box>
        <HPButton
          variant="outlined"
          color="primary"
          size="small"
          onClick={() => onRestore(broken.shared)}
        >
          Restore shared configuration
        </HPButton>
      </Box>
    </Box>
  );
};

export default TabTimeBreakWarning;
